/**
 * The start-screen reference strip (start-screen-unfolded-menu-strip design) — a full-width row
 * of six labeled glass tiles between the name slot and the mode cards, one per menu destination
 * not already visible on the screen: Help, Rules, Strategy, Glossary, Features, Your games.
 * Table-driven off `menu-entries.js`'s shared entry rows (this strip is the table's second
 * consumer, alongside `game-menu.js`) — every tile fires its own entry's `open("start")`.
 *
 * Filters the Leaderboard row out (features-entry-points design § 2) — this screen already
 * renders the board inline, so a seventh tile duplicating it would be redundant, and the six-tile
 * count is a real constraint, not tidiness: `.gs-strip` hardcodes `repeat(6, 1fr)` (wrap:
 * `repeat(3, 1fr)`), and both the labeled↔wrap decision and PERFORMANCE's glare-sweep measurement
 * were taken at six strip tiles. The filter's key-string coupling (`leaderboardHeading` doubles
 * as row identity) is accepted — `game-start-reference-strip.test.js` pins it.
 *
 * Presentation only. The labeled-single-row ↔ labeled-3×2-wrap switch is pure CSS keyed on the
 * `.gs-strip--wrap` class the placement pass sets (`game-start-layout.js#fitsLabeled` decides;
 * `.gs-strip`/`.gs-strip--wrap` in skat.css apply it) — no layout math lives here, and this module
 * doesn't even know the switch exists.
 */
import { div } from "../utils.js";
import { t } from "../i18n.js";
import { MENU_ENTRIES } from "../menu-entries.js";

// why: the icon's build-time SVG attr size. CSS (`.gs-strip-tile > svg`, skat.css) is the real
// elastic sizing source (`calc(44px * var(--gs-ux, 1))`) and overrides this attribute outright —
// sizing the svg element itself, not a wrapper box, is load-bearing (the mode-card icon shipped
// the wrapper-only version of this bug: the box shrinks with --gs-ux, the child svg's own
// attribute size doesn't, and the icon overflows onto the label at small multipliers). This
// constant only matters as the pre-CSS/no-stylesheet fallback size, so it doesn't itself carry a
// play-test mark — the play-test value lives once, in the CSS rule that actually renders it.
const ICON_BUILD_SIZE = 44;

/** The tiles this module mints, named where they are minted: both of the strip's own gestures
 *  select on it — the entrance's TILES group (animation/components/glass-condense.js) and the
 *  play-exit flight (game-start-strip-flight.js) — and neither owns the class. */
export const STRIP_TILES_SELECTOR = ".gs-strip-tile";

function buildTile(entry) {
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "icon-tile gs-strip-tile";
  const icon = entry.icon(ICON_BUILD_SIZE);
  icon.setAttribute("aria-hidden", "true"); // decorative — the label carries the accessible name
  tile.appendChild(icon);
  const label = document.createElement("span");
  label.className = "gs-strip-tile-label";
  label.textContent = t(entry.labelKey);
  tile.appendChild(label);
  tile.addEventListener("click", () => entry.open("start"));
  return tile;
}

/** Builds the strip row: six `.icon-tile` tiles, `menu-entries.js`'s own table order minus
 *  Leaderboard (see the header comment above). */
export function buildReferenceStrip() {
  const strip = div("gs-strip");
  for (const entry of MENU_ENTRIES.filter(
    (e) => e.labelKey !== "leaderboardHeading",
  )) {
    strip.appendChild(buildTile(entry));
  }
  return strip;
}
